import { useEffect, useState } from 'react';
import { isFacebookOrigin, parseEmbeddedSignupMessage } from '../utils/embeddedSignup';

/**
 * Loads Meta's JavaScript SDK and launches WhatsApp Embedded Signup v4.
 * The app and configuration IDs are public runtime configuration returned by
 * the server; the Facebook app secret always remains server-side.
 */
export function useFacebookSDK({ appId, configurationId, graphApiVersion } = {}) {
  const [sdkReady, setSdkReady] = useState(false);
  const [sdkError, setSdkError] = useState('');

  useEffect(() => {
    if (!appId || !configurationId) {
      setSdkReady(false);
      return undefined;
    }

    let active = true;
    const initialize = () => {
      if (!active || !window.FB) return;
      window.FB.init({
        appId,
        autoLogAppEvents: true,
        xfbml: true,
        version: graphApiVersion || 'v26.0',
      });
      setSdkError('');
      setSdkReady(true);
    };

    if (window.FB) {
      initialize();
      return () => { active = false; };
    }

    window.fbAsyncInit = initialize;
    let script = document.getElementById('facebook-jssdk');
    if (!script) {
      script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      script.onerror = () => {
        if (active) setSdkError('Could not load the Facebook SDK. Check your connection and try again.');
      };
      document.body.appendChild(script);
    }

    return () => { active = false; };
  }, [appId, configurationId, graphApiVersion]);

  function launchEmbeddedSignup(onSuccess, onError) {
    if (!appId || !configurationId) {
      onError('Meta Embedded Signup v4 has not been configured by the administrator.');
      return;
    }
    if (!window.FB || !sdkReady) {
      onError(sdkError || 'Facebook SDK is still loading. Please wait a moment and try again.');
      return;
    }

    const shared = { code: null, session: null };
    let settled = false;
    let sessionTimer = null;

    function cleanup() {
      window.removeEventListener('message', onMessage);
      if (sessionTimer) window.clearTimeout(sessionTimer);
    }

    function settle(callback) {
      if (settled) return;
      settled = true;
      cleanup();
      callback();
    }

    function tryResolve() {
      if (!shared.code || !shared.session) return;
      settle(() => onSuccess({ code: shared.code, ...shared.session }));
    }

    function onMessage(event) {
      if (!isFacebookOrigin(event.origin)) return;
      const result = parseEmbeddedSignupMessage(event.data);
      if (!result) return;

      if (result.type === 'success') {
        shared.session = result.data;
        tryResolve();
      } else {
        settle(() => onError(result.message));
      }
    }

    window.addEventListener('message', onMessage);

    // FB.login must run synchronously inside the user's click handler or the
    // browser may block the popup. v4 keeps assets and permissions in the
    // Facebook Login for Business configuration instead of this call.
    window.FB.login((response) => {
      if (!response.authResponse?.code) {
        settle(() => onError('Facebook login was cancelled or failed. Please try again.'));
        return;
      }

      shared.code = response.authResponse.code;
      tryResolve();
      if (!shared.session) {
        // Meta occasionally closes the popup without delivering the FINISH
        // postMessage. The authorization code is still usable for only a short
        // time, so let the server recover the WABA and phone IDs from the token
        // instead of leaving the user at a false-negative timeout.
        sessionTimer = window.setTimeout(() => {
          settle(() => onSuccess({
            code: shared.code,
            waba_id: null,
            phone_number_id: null,
            business_id: null,
          }));
        }, 1500);
      }
    }, {
      config_id: configurationId,
      response_type: 'code',
      override_default_response_type: true,
      extras: { setup: {} },
    });
  }

  return { sdkReady, sdkError, launchEmbeddedSignup };
}
